import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, NgZone, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { ColDef, GridApi, GridReadyEvent, GridState, ICellRendererParams, StateUpdatedEvent } from 'ag-grid-community';
import { Subject, catchError, combineLatest, of, startWith, switchMap, tap } from 'rxjs';
import { MenuStateService } from '../../menu-state.service';
import { ShipmentPlanningService } from '../../trips-and-shipments/planning.service';
import { TripDetails, TripDetailSummary, TripDetailShipment } from '../../trips-and-shipments/planning.models';
import { UserGridStateService } from '../../user-grid-state.service';

type Count = number | string | null;
type TripStoreRow = TripDetailShipment;
@Component({
  selector: 'app-full-trip-details',
  templateUrl: './full-trip-details.component.html',
  styleUrls: ['./full-trip-details.component.scss']
})
export class FullTripDetailsComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly refreshes = new Subject<void>();
  private readonly gridId = 'full_trip_details_api2';
  private gridApi?: GridApi<TripStoreRow>;
  trip_uuid = '';
  details: Partial<TripDetailSummary> = {};
  rowData: TripStoreRow[] = [];
  loading = true;
  hasDetails = false;
  detailError = '';
  storesError = '';
  gridReady = false;
  dark = false;
  search = '';
  displayedRows = 0;
  initialState?: GridState;
  counts: TripDetails['counts'] = { shipments: 0, resolved: 0, needs_mapping: 0, exception_groups: 0 };
  canEdit = false;
  snapshotAt = '';
  selectedShipment: TripStoreRow | null = null;
  editor: { kind: 'trip' | 'shipment'; uuid: string; version: string; title: string } | null = null;
  editValues = { week_no: '', manifest_carton_count: '', bol_carton_count: '', act_trail_number: '', external_bill_of_lading: '' };
  saving = false;
  saveError = '';
  savedMessage = '';

  readonly defaultColDef: ColDef<TripStoreRow> = {
    sortable: true, filter: 'agTextColumnFilter', resizable: true, editable: false,
    minWidth: 90, filterParams: { caseSensitive: false },
    valueFormatter: params => this.displayValue(params.value)
  };
  readonly shipmentColumnDefs: ColDef<TripStoreRow>[] = [
    { field: 'store_label', headerName: 'Store / exception', width: 175, pinned: 'left', lockPinned: true, cellClass: 'trip-store-number' },
    { field: 'customer_name', headerName: 'Saved customer', width: 175 },
    { colId: 'destination', headerName: 'City / region', width: 220,
      valueGetter: params => this.destinationLabel(params.data), tooltipValueGetter: params => this.mappingDescription(params.data) },
    { colId: 'mapping', headerName: 'Destination mapping', width: 190,
      valueGetter: params => this.mappingLabel(params.data), tooltipValueGetter: params => this.mappingDescription(params.data) },
    { field: 'status_labels', headerName: 'Receiving / audit', width: 200, wrapText: true, autoHeight: true,
      valueFormatter: params => (params.value as string[] || []).join('; '),
      cellRenderer: (params: ICellRendererParams<TripStoreRow>) => this.renderStatus(params.value) },
    { field: 'recorded_cartons', headerName: 'Cartons', width: 105, filter: 'agNumberColumnFilter', type: 'numericColumn' },
    { field: 'received_cartons', headerName: 'Received', width: 115, filter: 'agNumberColumnFilter', type: 'numericColumn' },
    { field: 'audited_cartons', headerName: 'Audited', width: 105, filter: 'agNumberColumnFilter', type: 'numericColumn' },
    { field: 'expected_cartons', headerName: 'Declared', width: 115, filter: 'agNumberColumnFilter', type: 'numericColumn',
      hide: true, valueFormatter: params => this.formatCount(params.value) },
    { field: 'held_cartons', headerName: 'Disposition', width: 120, filter: 'agNumberColumnFilter', type: 'numericColumn', hide: true },
    { field: 'loose_cartons', headerName: 'Without pallets', width: 145, filter: 'agNumberColumnFilter', type: 'numericColumn', hide: true },
    { field: 'asn_number', headerName: 'Shipment ASN', width: 170, hide: true },
    { field: 'alternate_reference', headerName: 'Work order', width: 130, hide: true,
      cellRenderer: (params: ICellRendererParams<TripStoreRow>) => this.renderOrderLink(params) },
    { field: 'reported_weight', headerName: 'Reported weight', width: 150, filter: 'agNumberColumnFilter', type: 'numericColumn', hide: true },
    { field: 'act_trail_number', headerName: 'ACT trailer', width: 145, hide: true },
    { field: 'external_bill_of_lading', headerName: 'Bill of lading', width: 175, hide: true },
    { field: 'week_no', headerName: 'Week', width: 90, hide: true },
    { field: 'created', headerName: 'Scan date', width: 190, hide: true, valueFormatter: params => this.formatDate(params.value) },
    { colId: 'appointment_day', headerName: 'Appointment day', width: 160, hide: true, valueGetter: params => params.data?.destination?.appointment_day },
    { colId: 'appointment_start', headerName: 'Window start', width: 130, hide: true, valueGetter: params => params.data?.destination?.appointment_start },
    { colId: 'appointment_end', headerName: 'Window end', width: 130, hide: true, valueGetter: params => params.data?.destination?.appointment_end },
    { colId: 'appointment_contact', headerName: 'Appointment contact', width: 180, hide: true, valueGetter: params => params.data?.destination?.appointment_contact },
    { colId: 'loading_dock', headerName: 'Loading dock', width: 130, hide: true,
      valueGetter: params => params.data?.destination?.loading_dock, valueFormatter: params => this.formatDock(params.value) },
    { field: 'shipment_list_uuid', headerName: 'Shipment UUID', hide: true },
    { colId: 'actions', headerName: 'Actions', width: 190, minWidth: 190, pinned: 'right', lockPinned: true,
      sortable: false, filter: false, resizable: false, suppressMovable: true,
      cellRenderer: (params: ICellRendererParams<TripStoreRow>) => this.renderActions(params),
      suppressKeyboardEvent: params => params.event.key === 'Enter' && params.event.target instanceof HTMLButtonElement }
  ];

  constructor(private readonly route: ActivatedRoute, private readonly planning: ShipmentPlanningService,
    private readonly zone: NgZone, private readonly gridStateService: UserGridStateService, readonly theme: MenuStateService) {}

  ngOnInit(): void {
    this.theme.themeState$.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.dark = value === 'ams-content-dark');
    this.gridStateService.getUserGridState(this.gridId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: state => { this.initialState = state || undefined; this.gridReady = true; },
      error: () => { this.gridReady = true; }
    });
    combineLatest([this.route.paramMap, this.refreshes.pipe(startWith(undefined))]).pipe(
      tap(([params]) => {
        this.trip_uuid = params.get('uuid') || '';
        this.loading = true; this.hasDetails = false; this.detailError = ''; this.storesError = '';
        this.details = {}; this.rowData = []; this.displayedRows = 0;
      }),
      switchMap(() => this.planning.tripDetails(this.trip_uuid).pipe(catchError((error: HttpErrorResponse) => {
        this.detailError = typeof error.error?.message === 'string' ? error.error.message : 'The trip could not be loaded. Please refresh.';
        return of(null);
      }))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(response => {
      this.loading = false;
      if (!response?.ok) { if (!this.detailError) this.detailError = 'No valid trip details were returned.'; return; }
      this.details = response.trip; this.hasDetails = true;
      this.rowData = response.shipments; this.displayedRows = this.rowData.length;
      this.counts = response.counts; this.canEdit = response.capabilities.edit_metadata; this.snapshotAt = response.snapshot_at;
      this.updateDeclaredColumn();
      if (this.selectedShipment) this.selectedShipment = this.rowData.find(row => row.shipment_list_uuid === this.selectedShipment?.shipment_list_uuid) || null;
    });
  }

  refresh(): void { if (!this.editor && !this.saving) this.refreshes.next(); }
  onGridReady(event: GridReadyEvent<TripStoreRow>): void { this.gridApi = event.api; this.updateDeclaredColumn(); }
  onFilterChanged(): void { this.displayedRows = this.gridApi?.getDisplayedRowCount() || 0; }
  onStateUpdated(event: StateUpdatedEvent<TripStoreRow>): void {
    if (event.sources.includes('columnSizing') || event.sources.includes('columnOrder')) {
      this.gridStateService.saveUserGridState(this.gridId, event.state);
    }
  }
  exportToCSV(): void {
    this.gridApi?.exportDataAsCsv({ fileName: `trip-${this.details.trip_number || 'shipments'}.csv`,
      columnSeparator: '|', columnKeys: this.exportColumns });
  }
  exportToExcel(): void {
    this.gridApi?.exportDataAsExcel({ fileName: `trip-${this.details.trip_number || 'shipments'}.xlsx`, columnKeys: this.exportColumns });
  }
  private get exportColumns(): string[] {
    return this.shipmentColumnDefs.flatMap(column => column.field ? [column.field] : (column.colId && column.colId !== 'actions' ? [column.colId] : []));
  }

  displayValue(value: unknown, fallback = '—'): string {
    if (value === null || value === undefined) return fallback;
    const text = String(value).trim();
    if (!text || text === ',' || text.toLowerCase() === 'null') return fallback;
    return text;
  }
  formatCount(value: unknown): string {
    if (this.displayValue(value) === '—') return '—';
    const number = Number(value);
    return Number.isFinite(number) ? number.toLocaleString('en-US', { maximumFractionDigits: 2 }) : '—';
  }
  formatDate(value: unknown): string {
    const text = this.displayValue(value);
    if (text === '—') return text;
    // The legacy API returns local timestamps without a timezone. Keep that timezone unchanged.
    const date = new Date(text.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1'));
    return Number.isNaN(date.getTime()) ? text : date.toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
    });
  }
  formatDock(value: unknown): string {
    if (value === true || value === 1 || value === '1' || value === 'true' || value === 't') return 'Yes';
    if (value === false || value === 0 || value === '0' || value === 'false' || value === 'f') return 'No';
    return 'Unknown';
  }
  progress(value: Count | undefined): number {
    const total = Number(this.details.recorded_cartons);
    const count = Number(value);
    if (!Number.isFinite(total) || total <= 0 || !Number.isFinite(count)) return 0;
    return Math.max(0, Math.min(100, count / total * 100));
  }
  get hasCartonTotal(): boolean { return Number(this.details.recorded_cartons) > 0; }

  private renderStatus(value: unknown): HTMLElement {
    const wrapper = document.createElement('div');
    wrapper.className = 'trip-status-list';
    const statuses: string[] = Array.isArray(value) ? value : [];
    if (!statuses.length) { wrapper.textContent = 'Not available'; wrapper.classList.add('trip-cell-muted'); return wrapper; }
    for (const status of statuses) {
      const badge = document.createElement('span');
      badge.className = 'trip-status';
      if (status.startsWith('Audited:') || status.startsWith('Received:')) badge.classList.add('trip-status-complete');
      else if (status.includes('pending') || status.startsWith('Out of market') || status.startsWith('Needs disposition')) badge.classList.add('trip-status-progress');
      else if (status.startsWith('Receiving:') || status.startsWith('Auditing:')) badge.classList.add('trip-status-created');
      badge.textContent = status;
      wrapper.appendChild(badge);
    }
    return wrapper;
  }
  private renderOrderLink(params: ICellRendererParams<TripStoreRow>): HTMLElement {
    const reference = this.displayValue(params.value);
    const uuid = params.data?.lading_uuid;
    if (reference === '—' || !this.isUuid(uuid) || !params.data?.order_link_valid) {
      const span = document.createElement('span'); span.textContent = reference; return span;
    }
    const link = document.createElement('a');
    link.href = `/full-order-details/${uuid}`; link.textContent = reference; link.className = 'trip-grid-link';
    return link;
  }
  private renderActions(params: ICellRendererParams<TripStoreRow>): HTMLElement {
    const wrapper = document.createElement('div'); wrapper.className = 'trip-row-actions';
    const shipment = params.data;
    if (!shipment || !this.isUuid(shipment.shipment_list_uuid)) return wrapper;
    const review = document.createElement('button');
    review.type = 'button'; review.textContent = shipment.destination ? 'Details' : 'Review'; review.className = 'trip-grid-button';
    review.setAttribute('aria-label', `Review ${shipment.store_label}`);
    review.addEventListener('click', () => this.zone.run(() => { this.selectedShipment = shipment; }));
    wrapper.append(review);
    if (shipment.destination && shipment.held_cartons === 0) {
      const plan = document.createElement('a');
      plan.href = `/trips-and-shipments/shipments/${shipment.shipment_list_uuid}/plan`;
      plan.textContent = 'Plan'; plan.className = 'trip-grid-button trip-grid-button-accent';
      plan.setAttribute('aria-label', `Plan shipment for store ${shipment.store_label}`);
      wrapper.append(plan);
    }
    return wrapper;
  }

  private isUuid(value: unknown): value is string {
    return typeof value === 'string' && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  }
  private updateDeclaredColumn(): void {
    this.gridApi?.setColumnsVisible(['expected_cartons'], this.rowData.some(row => row.expected_cartons !== null));
    this.gridApi?.setColumnsVisible(['alternate_reference'], this.rowData.some(row => row.order_link_valid));
  }
  destinationLabel(row?: TripStoreRow): string {
    if (!row) return '';
    if (row.destination) return [row.destination.city, row.destination.region].filter(Boolean).join(', ') || 'Address needs a city';
    return row.mapping_state === 'exception' ? 'Disposition required' : 'Location needs review';
  }
  mappingLabel(row?: TripStoreRow): string {
    if (!row) return '';
    const labels = { saved: 'Saved store location', resolved: 'Matched in Pool Manager', missing: 'Missing location',
      ambiguous: 'Multiple locations', conflict: 'Identity conflict', exception: 'Exception cartons', invalid: 'Invalid store address' };
    return labels[row.mapping_state];
  }
  mappingDescription(row?: TripStoreRow): string {
    return row?.mapping_issues.map(issue => issue.message).join(' ') || this.mappingLabel(row);
  }
  editTripDetails(): void {
    if (!this.canEdit || !this.details.metadata_version || this.editor) return;
    this.saveError = ''; this.savedMessage = '';
    this.editor = { kind: 'trip', uuid: this.trip_uuid, version: this.details.metadata_version, title: 'Edit trip references' };
    this.editValues.week_no = this.details.week_no || '';
    this.editValues.manifest_carton_count = this.details.manifest_carton_count || '';
    this.editValues.bol_carton_count = this.details.bol_carton_count || '';
  }
  editShipment(shipment: TripStoreRow): void {
    if (!this.canEdit || this.editor) return;
    this.saveError = ''; this.savedMessage = '';
    this.editor = { kind: 'shipment', uuid: shipment.shipment_list_uuid, version: shipment.metadata_version, title: `Edit references for ${shipment.store_label}` };
    this.editValues.act_trail_number = shipment.act_trail_number || '';
    this.editValues.external_bill_of_lading = shipment.external_bill_of_lading || '';
  }
  cancelEditor(): void { if (!this.saving) { this.editor = null; this.saveError = ''; } }
  saveMetadata(): void {
    const editor = this.editor;
    if (!editor || this.saving) return;
    this.saving = true; this.saveError = '';
    const request = editor.kind === 'trip'
      ? this.planning.saveTripMetadata(editor.uuid, { metadata_version: editor.version,
          week_no: this.editValues.week_no || null, manifest_carton_count: this.editValues.manifest_carton_count || null,
          bol_carton_count: this.editValues.bol_carton_count || null })
      : this.planning.saveShipmentMetadata(editor.uuid, { metadata_version: editor.version,
          act_trail_number: this.editValues.act_trail_number || null, external_bill_of_lading: this.editValues.external_bill_of_lading || null });
    request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: response => {
        this.saving = false;
        if (!response.ok) { this.saveError = 'The reference change could not be saved.'; return; }
        this.editor = null; this.savedMessage = 'References saved.'; this.refreshes.next();
      },
      error: (error: HttpErrorResponse) => {
        this.saving = false;
        this.saveError = typeof error.error?.message === 'string' ? error.error.message : 'The references could not be saved. Please retry.';
      }
    });
  }
}
